"use client";

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { motion, useMotionValue } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageLPLongSchema } from "@/sanity/pages/PageLPLongTemplate";

import dots from "@/public/vsl-short/dots.svg";

// card accent colors from Figma, cycled when an item has no explicit color
const ACCENT_COLORS = ["#D9F9B9", "#B9C0F9", "#F9E0B9", "#E9B9F9", "#B9F0F9"];

export default function StepsSection({ data, className }: { data: PageLPLongSchema; className?: string }) {
  const items = data?.stepsItems ?? [];
  const stacked = stegaClean(data?.stepsLayout) !== "horizontal";

  // desktop: the section pins while the card track slides horizontally
  const sectionRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);

  // scrollRange = how far the track must travel to reveal its last card
  // pinHeight = natural height of the locked screen, so the runway below it is exact
  const [scrollRange, setScrollRange] = useState(0);
  const [pinHeight, setPinHeight] = useState(0);
  const [current, setCurrent] = useState(0);

  const x = useMotionValue(0);

  const scheduled = useRef(false);
  const scheduleRecalc = useCallback(() => {
    if (scheduled.current) return;
    scheduled.current = true;
    requestAnimationFrame(() => {
      scheduled.current = false;
      const viewportWidth = viewportRef.current?.clientWidth ?? 0;
      const trackWidth = trackRef.current?.scrollWidth ?? 0;
      setScrollRange(Math.max(0, trackWidth - viewportWidth));
      setPinHeight(viewportRef.current?.offsetHeight ?? 0);
    });
  }, []);

  useLayoutEffect(() => {
    scheduleRecalc();
  }, [items.length, stacked, scheduleRecalc]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (stacked || !viewport || !track) return;

    const observer = new ResizeObserver(() => scheduleRecalc());
    observer.observe(viewport);
    observer.observe(track);

    window.addEventListener("resize", scheduleRecalc, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", scheduleRecalc);
    };
  }, [stacked, scheduleRecalc]);

  /*
    While the section is pinned its top edge sits at the viewport top, so its
    own offset from the top is exactly how far it has been scrolled through —
    0 at the start of the pin, -scrollRange at the end. No page offsets or
    viewport-height math needed, which keeps it correct after any layout shift.
  */
  useEffect(() => {
    const section = sectionRef.current;
    if (stacked || !section) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      if (scrollRange <= 0) {
        x.set(0);
        setCurrent(0);
        return;
      }
      const travelled = Math.min(Math.max(-section.getBoundingClientRect().top, 0), scrollRange);
      x.set(-travelled);
      setCurrent(Math.round((travelled / scrollRange) * Math.max(0, items.length - 1)));
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [stacked, scrollRange, items.length, x]);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [focused, setFocused] = useState(0);

  useEffect(() => {
    if (!stacked) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      const middle = window.innerHeight / 2;
      let closest = 0;
      let closestDistance = Infinity;
      cardRefs.current.forEach((card, index) => {
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - middle);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = index;
        }
      });
      setFocused(closest);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [stacked, items.length]);

  const renderCard = (item: (typeof items)[number], index: number) => {
    const accent = item?.color || ACCENT_COLORS[index % ACCENT_COLORS.length];
    const number = String(index + 1).padStart(2, "0");

    return (
      <div
        ref={
          stacked
            ? (card) => {
                cardRefs.current[index] = card;
              }
            : undefined
        }
        className={cn(
          "flex h-full w-full flex-col",
          stacked && "transition-[filter] duration-500 ease-out motion-reduce:transition-none",
          stacked && focused !== index && "blur-[0.75rem]",
        )}>
        {/* accent top bar */}
        <div
          className="h-[0.5rem] w-full"
          style={{ backgroundColor: accent }}
        />

        <div className={cn("flex flex-1 flex-col bg-white lg:h-[43.75rem] lg:flex-row", stacked && "lg:flex-none")}>
          {/* number panel — layered ghost copies behind the number, like the Figma echo effect */}
          <div
            className={cn(
              "relative flex h-[20rem] w-full shrink-0 flex-col items-start justify-end overflow-clip bg-[#0A1A1D] pt-[1.6rem] pb-[1rem] pl-[1.6rem] lg:h-auto lg:w-[34.875rem] lg:pt-[1.75rem] lg:pl-[1.75rem]",
              stacked && "lg:pb-[1.625rem]",
            )}>
            <div
              className={cn(
                "absolute hidden items-center gap-[0.75rem] lg:flex",
                stacked ? "top-[2rem] left-[2rem]" : "top-[1.75rem] left-[1.75rem]",
              )}>
              {items.map((_, dotIndex) => (
                <span
                  key={dotIndex}
                  className="h-[1.375rem] w-[1.375rem]"
                  style={{
                    backgroundColor: dotIndex === (stacked ? index : current) ? accent : `${accent}33`,
                  }}
                />
              ))}
            </div>
            {/* position dots — mobile stack highlights this card's own step */}
            <div className="absolute top-[1.6rem] left-[1.6rem] flex items-center gap-[0.6rem] lg:hidden">
              {items.map((_, dotIndex) => (
                <span
                  key={dotIndex}
                  className="h-[1.1rem] w-[1.1rem]"
                  style={{
                    backgroundColor: dotIndex === index ? accent : `${accent}33`,
                  }}
                />
              ))}
            </div>

            {/*
              Number stack: the solid copy sits in normal flow so `justify-end` on the panel
              anchors it to the bottom — `bottom-*` is unreliable for absolute children here.
              The two faded copies are pulled upward from the same top edge and painted first,
              so each lower copy's dark background masks the one above it (the Figma echo).
            */}
            <div className="relative w-fit">
              {[
                {
                  color: `${accent}38`,
                  shift: "-translate-y-[3.5rem]",
                },
                {
                  color: `${accent}7A`,
                  shift: "-translate-y-[1.875rem]",
                },
              ].map((ghost, ghostIndex) => (
                <p
                  key={ghostIndex}
                  className={cn(
                    "absolute top-0 left-0 m-0 bg-[#0A1A1D] py-[0.625rem] font-funnel font-medium text-[12.5rem] lg:text-[18.75rem] leading-none tracking-[-0.04em] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]",
                    ghost.shift,
                  )}
                  style={{ color: ghost.color }}>
                  {number}
                </p>
              ))}
              <p
                className="relative m-0 bg-[#0A1A1D] py-[0.625rem] font-funnel text-[12.5rem] leading-none font-medium tracking-[-0.04em] [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] lg:text-[18.75rem]"
                style={{ color: accent }}>
                {number}
              </p>
            </div>
          </div>

          {/* content panel */}
          <div className="flex flex-1 flex-col justify-between gap-[6rem] px-[1.3rem] py-[2rem] lg:gap-[5rem] lg:p-[5rem]">
            {item?.title && (
              <p className="m-0 w-[min(42.75rem,100%)] font-funnel text-[2.6rem] leading-none font-medium text-black lg:text-[5rem]">
                {item.title}
              </p>
            )}

            {item?.list && item.list.length > 0 && (
              <ul className="flex w-full flex-col gap-[0.5rem]">
                {item.list.map((entry, entryIndex) => (
                  <li
                    key={entryIndex}
                    className="flex items-stretch">
                    <span
                      className="flex w-[3.2rem] shrink-0 items-center justify-center p-[0.8rem] lg:w-[4rem] lg:p-[1rem]"
                      style={{ backgroundColor: accent }}>
                      {entry?.icon?.src && (
                        <Image
                          className="!h-[2.5rem] !w-[2.5rem] object-contain"
                          src={entry.icon}
                          alt=""
                        />
                      )}
                    </span>
                    <span className="flex flex-1 items-center bg-[#F3F2F1] p-[0.8rem] lg:p-[1rem]">
                      <span className="font-geist text-[2.25rem] leading-[1.5] text-black/80">{entry?.text}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className={cn("relative bg-[#F3F2F1]", className)}>
      {stacked && (
        <>
          <DecorationLine
            className="top-0 hidden lg:left-[8.85%] lg:block"
            orientation="vertical"
            style="dark"
          />
          <DecorationLine
            className="top-0 hidden lg:right-[8.85%] lg:block"
            orientation="vertical"
            style="dark"
          />
        </>
      )}

      {/*
        On desktop this wrapper adds the vertical runway the pin needs: its height is
        the locked screen plus the track's horizontal overflow, so scrolling through
        the runway moves the cards exactly to their end. On mobile it stays auto.
      */}
      <div
        ref={sectionRef}
        className={cn("relative w-full", !stacked && "lg:h-[var(--steps-runway)]")}
        style={
          {
            "--steps-runway": pinHeight ? `${pinHeight + scrollRange}px` : "auto",
          } as React.CSSProperties
        }>
        {/* the locked screen — keeps its normal layout, just held in place */}
        <div
          ref={viewportRef}
          className={cn("w-full", !stacked && "lg:sticky lg:top-0 lg:overflow-hidden")}>
          <div
            className={cn(
              "relative flex flex-col items-center px-[2rem] pt-[8rem] pb-[5.625rem] text-center sm:px-[3rem] md:px-[5rem]",
              stacked ? "lg:pt-[14.625rem]" : "lg:pt-[15.75rem]",
            )}>
            <Image
              src={dots}
              alt=""
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute top-0 left-1/2 !w-full -translate-x-1/2 select-none lg:!w-[119.875rem]",
                stacked && "lg:hidden",
              )}
            />

            <DecorationLine
              className={cn("top-0 left-[1rem] lg:left-[8.85%]", stacked && "lg:hidden")}
              orientation="vertical"
              style="dark"
            />
            <DecorationLine
              className={cn("top-0 right-[1rem] lg:right-[8.85%]", stacked && "lg:hidden")}
              orientation="vertical"
              style="dark"
            />
            {!stacked && (
              <DecorationLine
                className="bottom-0 hidden lg:block"
                orientation="horizontal"
                style="dark"
              />
            )}

            {data?.stepsChip?.text && (
              <Chip
                className={cn(
                  "relative z-[1] mb-[2.75rem] [&_p]:font-geistmono [&_p]:text-[#202223] [&_p]:uppercase",
                  stacked && "lg:mb-[3.25rem]",
                )}
                image={data?.stepsChip?.icon}
                text={data?.stepsChip?.text}
              />
            )}

            {data?.stepsTitle && (
              <div className="pixel-highlight richtext richtext-center relative z-[1] w-[min(83rem,100%)] prose-em:!px-0 prose-em:font-ivy prose-em:text-[4rem] sm:prose-em:text-[5.5rem] lg:prose-em:!px-[.7rem] lg:prose-em:text-[6.5rem] prose-headings:font-funnel prose-headings:text-[4rem] prose-headings:leading-none prose-headings:font-medium prose-headings:text-balance prose-headings:text-black sm:prose-headings:text-[5.5rem] lg:prose-headings:text-[6.5rem]">
                <PortableText
                  value={data.stepsTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {items.length > 0 && (
            <>
              <div
                className={cn(
                  "relative flex flex-col gap-[1.6rem] px-[3rem]",
                  stacked ? "lg:mx-auto lg:mt-[6.125rem] lg:w-[101.25rem] lg:gap-[3.25rem] lg:px-0" : "lg:hidden",
                )}>
                <DecorationLine
                  className="top-0 left-[1rem] lg:hidden"
                  orientation="vertical"
                  style="dark"
                />
                <DecorationLine
                  className="top-0 right-[1rem] lg:hidden"
                  orientation="vertical"
                  style="dark"
                />
                {items.map((item, index) => (
                  <React.Fragment key={index}>{renderCard(item, index)}</React.Fragment>
                ))}
              </div>

              {/*
                desktop — the card track slides horizontally as the section stays locked.
                Clipping lives here rather than on the locked screen so only the track's
                horizontal overflow is hidden and the CTA below can never be cut off.
              */}
              {!stacked && (
                <div className="mt-[6.125rem] hidden overflow-hidden lg:block">
                  <motion.ul
                    ref={trackRef}
                    style={{ x }}
                    className="my-0 flex w-max list-none items-stretch gap-[3.25rem] pr-[30rem] pl-[40rem] will-change-transform">
                    {items.map((item, index) => (
                      <li
                        key={index}
                        className="w-[min(101.25rem,92vw)] shrink-0">
                        {renderCard(item, index)}
                      </li>
                    ))}
                  </motion.ul>
                </div>
              )}
            </>
          )}

          {data?.stepsCTA && (
            <div
              className={cn(
                "relative flex justify-center px-[3rem] pt-[5rem] pb-[7rem] lg:px-0",
                stacked ? "lg:pt-[11.75rem] lg:pb-[9.5rem]" : "lg:hidden",
              )}>
              <DecorationLine
                className="top-0 left-[1rem] lg:left-[8.85%] lg:hidden"
                orientation="vertical"
                style="dark"
              />
              <DecorationLine
                className="top-0 right-[1rem] lg:right-[8.85%] lg:hidden"
                orientation="vertical"
                style="dark"
              />
              <CTACornerButton data={data.stepsCTA} />
            </div>
          )}
        </div>
      </div>

      <DecorationLine
        className={cn("bottom-0", stacked && "lg:hidden")}
        orientation="horizontal"
        style="dark"
      />
    </section>
  );
}
